// CDP / Visitor Intelligence KPI card — the house stat tile used across the CDP
// dashboard. Bound to the Guest Intelligence typography tokens (.gi-card-label,
// .gi-number, .gi-hero-number) per Epic 6, with a 3px accent top-border, a faint
// watermark icon, a DeltaBadge pill in the title row, and a bottom-pinned
// sparkline.
//
// Extracted verbatim from CdpDashboardPage.js so EVERY CDP surface renders the
// exact same card anatomy — sizes, fonts, spacing, colors — as the eight
// headline KPI tiles. The People tab (CdpPeopleTab) uses this so its
// "Identified Visitors" stat matches the dashboard cards byte-for-byte.
//
// This is deliberately separate from components/shared/KpiCard.js (the Ads/GA4
// card): that one uses responsive Tailwind sizes (text-2xl sm:text-3xl) and a
// precomputed changePct, while the CDP card is wired to the .gi-* tokens and
// computes its own delta from current/previous. The two designs are
// intentionally distinct (see docs/VISITOR-INTELLIGENCE-EPICS.md, Epic 6).
//
// `deltaNode` (optional): a caller-supplied pill rendered in place of the
// relative-% DeltaBadge — used by CdpPeopleTab, whose "Identified Visitors"
// delta is in percentage POINTS (pp), not a relative % change. When omitted
// (undefined), the standard DeltaBadge renders, so existing callers are
// unaffected. Pass `null` to render no pill at all.
import React, { useState } from "react";
import InfoTip from "../shared/InfoTip";
import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts";

export function computeDelta(current, previous) {
  if (!previous || previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

export const DeltaBadge = ({ current, previous }) => {
  if (!current && !previous) return null;
  if ((!current || current === 0) && (!previous || previous === 0)) return null;
  if (!previous || previous === 0) return null;
  if (!current || current === 0) return null;
  const delta = computeDelta(current, previous);
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  const colorClasses = isPositive
    ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
    : isNegative
    ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400"
    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400";
  const Arrow = isPositive ? (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
    </svg>
  ) : isNegative ? (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  ) : null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-semibold ${colorClasses}`}>
      {Arrow}
      {delta >= 0 ? "+" : ""}{delta}%
    </span>
  );
};

export const KPI_ACCENT_BORDERS = {
  primary: "border-t-[#32B1FF]",
  success: "border-t-teal-400 dark:border-t-teal-500",
  muted:   "border-t-sky-400 dark:border-t-sky-500",
  subtle:  "border-t-indigo-400 dark:border-t-indigo-500",
  warning: "border-t-amber-400 dark:border-t-amber-500",
  rose:    "border-t-rose-400 dark:border-t-rose-500",
};
export const KPI_ICON_COLORS = {
  primary: "text-[#32B1FF]/10 dark:text-[#32B1FF]/5",
  success: "text-teal-400/10 dark:text-teal-500/5",
  muted:   "text-sky-400/10 dark:text-sky-500/5",
  subtle:  "text-indigo-400/10 dark:text-indigo-500/5",
  warning: "text-amber-400/10 dark:text-amber-500/5",
  rose:    "text-rose-400/10 dark:text-rose-500/5",
};
export const KPI_ICONS = {
  dollar:  (<><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></>),
  booking:  (<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="M9 16l2 2 4-4" /></>),
  click:    (<><path d="M12 2a10 10 0 1 0 10 10" /><path d="M12 12l8-8" /><path d="M16 4h4v4" /></>),
  users:    (<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  chart:    (<><path d="M4 20V10" /><path d="M10 20V4" /><path d="M16 20v-6" /><path d="M22 20H2" /></>),
  play:     (<><polygon points="5 3 19 12 5 21 5 3" /></>),
  percent:  (<><line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></>),
  phone:    (<><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" /></>),
};
// 2026-08-20 — The AI takeaway sentence (e.g. "Bookings are up 175% versus the
// prior 7 days - 11 this period against 4 before.") used to render always-on
// below the subtitle. It now hides behind a small lightbulb "hint" button in
// the subtitle row; clicking it toggles an inline reveal. The sentence node is
// passed in verbatim (PanelTakeaway) and rendered unchanged when expanded.
const HintButton = ({ open, onToggle }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    aria-label={open ? "Hide insight" : "Show insight"}
    aria-expanded={open}
    title={open ? "Hide insight" : "Show insight"}
    className={`inline-flex items-center justify-center w-5 h-5 rounded-full transition-colors ${open ? "text-amber-500 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30" : "text-gray-400 dark:text-gray-500 hover:text-amber-500 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"}`}
  >
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6" />
    </svg>
  </button>
);

export const KpiCard = ({ title, value, subtitle, current, previous, variant = "primary", icon = "chart", loading = false, tooltip, sparkData, takeaway, hero = false, deltaNode, className = "" }) => {
  const [showHint, setShowHint] = useState(false);
  return (
    <div className={`relative bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 border-t-[3px] ${KPI_ACCENT_BORDERS[variant] ?? KPI_ACCENT_BORDERS.primary} p-6 pb-4 min-h-[140px] flex flex-col transition-shadow overflow-hidden ${className}`}>
      <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
        <div className={`absolute -top-6 -right-6 ${KPI_ICON_COLORS[variant] ?? KPI_ICON_COLORS.primary}`}>
          <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
            {KPI_ICONS[icon] ?? KPI_ICONS.chart}
          </svg>
        </div>
      </div>
      <div className="flex items-center flex-wrap gap-1.5 relative z-20">
        <div className="gi-card-label text-gray-500 dark:text-gray-400">{title}</div>
        {tooltip && <InfoTip text={tooltip} />}
        {!loading && (deltaNode !== undefined ? deltaNode : <DeltaBadge current={current} previous={previous} />)}
      </div>
      {loading ? (
        <div className="mt-2 flex items-center gap-2 relative z-10">
          <svg className="animate-spin h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-lg text-gray-400 dark:text-gray-500">Loading...</span>
        </div>
      ) : (
        <div className={`mt-2 ${hero ? "gi-hero-number" : "gi-number"} text-gray-900 dark:text-gray-100 relative z-10 break-words`}>{value}</div>
      )}
      {(subtitle || takeaway) && (
        <div className="mt-1.5 flex items-center gap-1.5 relative z-10">
          {subtitle && <span className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</span>}
          {takeaway && <HintButton open={showHint} onToggle={() => setShowHint((s) => !s)} />}
        </div>
      )}
      {takeaway && showHint && (
        <div className="mt-1.5 relative z-10 text-xs sm:text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/60 rounded-md px-2.5 py-1.5 border border-gray-100 dark:border-gray-800">
          {takeaway}
        </div>
      )}
      {sparkData && sparkData.length > 1 && !loading && (
        <div className="mt-auto pt-1 pb-3 -mx-6 relative z-10 overflow-hidden">
          <ResponsiveContainer width="100%" height={52}>
            <AreaChart data={sparkData} margin={{ top: 8, right: 0, left: 0, bottom: 6 }}>
              <defs>
                <linearGradient id={`spark-${title.split(" ").join("_")}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#94a3b8" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <YAxis hide domain={[(dataMin) => dataMin - (Math.abs(dataMin) * 0.05 || 1), (dataMax) => dataMax + (Math.abs(dataMax) * 0.15 || 1)]} />
              <Area type="natural" dataKey="v" stroke="#94a3b8" strokeWidth={1.2} fill={`url(#spark-${title.split(" ").join("_")})`} dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default KpiCard;

